// Descartar un comprobante que nunca se emitió — reemplaza la papelera «Eliminar».
//
// La papelera anterior estaba rota: se pintaba para por_emitir, rechazado,
// devuelto y error, pero `fe_eliminar` sólo borraba `por_emitir` y en los otros
// tres respondía «Comprobante no encontrado o ya emitido». Un devuelto no se
// podía emitir ni quitar: quedaba atrapado. Era, literalmente, la queja.
//
// Ahora no se borra nada: `fin_comprobante_descartar` (script 872) lo deja en
// estado `descartado` con motivo, autor y fecha, conservando la clave y la serie
// — porque la fila descartada es el recibo del número que se quemó.
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

// Mismo catálogo que `financiero._descarte_motivos()`.
const MOTIVOS: { codigo: string; label: string }[] = [
  { codigo: 'error_digitacion', label: 'Error de digitación' },
  { codigo: 'cliente_desistio', label: 'El cliente desistió' },
  { codigo: 'duplicado', label: 'Duplicado' },
  { codigo: 'prueba_demo', label: 'Dato de prueba o demostración' },
  { codigo: 'datos_incorrectos', label: 'Datos incorrectos' },
  { codigo: 'otro', label: 'Otro' },
];

export interface DocDescartable {
  id: string;
  serie_comprobante?: string | null;
  estado_emision?: string | null;
}

export function DescartarDialog({ doc, onClose, onDone }: {
  doc: DocDescartable;
  onClose: () => void;
  onDone: () => void;
}) {
  const [codigo, setCodigo] = useState('error_digitacion');
  const [motivo, setMotivo] = useState('');
  // Sólo aparece si la RPC contesta que el comprobante ya salió hacia el SRI:
  // es la rama C, la que no se puede resolver a ciegas.
  const [avisoSri, setAvisoSri] = useState<string | null>(null);
  const [verificado, setVerificado] = useState(false);

  const descartar = useMutation({
    mutationFn: async () =>
      (await apiClient.post('/rpc/fin_comprobante_descartar', {
        _ticket_id: doc.id, _motivo_codigo: codigo, _motivo: motivo.trim(),
        _verificado_en_sri: verificado,
      }, FIN)).data as { message: string; deja_hueco: boolean; rama: string },
    onSuccess: (r) => { toast.success(r.message); onDone(); onClose(); },
    onError: (e) => {
      const msg = getErrorMessage(e);
      if (/YA SALIÓ/i.test(msg)) setAvisoSri(msg); else toast.error(msg);
    },
  });

  const campo = 'w-full rounded-md border bg-background px-2.5 py-1.5 text-sm';
  const corto = motivo.trim().length < 10;

  return (
    <PanelIntegrado title="Descartar comprobante" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {doc.serie_comprobante
            ? <>Este documento ya reservó el número <strong className="font-mono">{doc.serie_comprobante}</strong>.
                Al descartarlo ese número no se usará nunca más y quedará como hueco justificado.</>
            : <>Este borrador no ha reservado número, así que descartarlo no deja ningún hueco en la secuencia.</>}
        </p>
        <p className="text-xs text-muted-foreground">
          No se borra nada: el comprobante queda como descartado, con tu nombre y el motivo. Esa es la
          única justificación que quedará del número no usado.
        </p>

        <label className="block text-xs text-muted-foreground">Motivo
          <select className={`${campo} mt-1`} value={codigo} onChange={(e) => setCodigo(e.target.value)}>
            {MOTIVOS.map((m) => <option key={m.codigo} value={m.codigo}>{m.label}</option>)}
          </select>
        </label>

        <label className="block text-xs text-muted-foreground">Explicación
          <textarea className={`${campo} mt-1`} rows={3} value={motivo} autoFocus
                    placeholder="Qué pasó con este comprobante (mínimo 10 caracteres)"
                    onChange={(e) => setMotivo(e.target.value)} />
          {corto && motivo.length > 0 && (
            <span className="text-[11px] text-amber-600">Faltan {10 - motivo.trim().length} caracteres</span>
          )}
        </label>

        {avisoSri && (
          <div className="rounded-md border border-red-500/50 bg-red-500/5 p-3 space-y-2">
            <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{avisoSri}</span>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5" checked={verificado}
                     onChange={(e) => setVerificado(e.target.checked)} />
              <span>Consulté esa clave en el portal del SRI y confirmo que <strong>no está autorizada</strong>.
                    Entiendo que esta afirmación queda registrada a mi nombre.</span>
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" isLoading={descartar.isPending}
                  disabled={corto || (!!avisoSri && !verificado)}
                  onClick={() => descartar.mutate()}>
            {descartar.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Descartar
          </Button>
        </div>
      </div>
    </PanelIntegrado>
  );
}
